import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, User2, Receipt, Users2, CornerDownLeft } from "lucide-react";

/**
 * Header quick-search. Debounced 250 ms; shows the top 8 hits across
 * customers, invoices and (admin only) partners. Keyboard nav: ↑↓ to
 * move, ⏎ to open, Esc to close.
 */
export default function HeaderSearch({ testid = "header-search" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/commissions/search", { params: { q } });
        setResults(data.results || []);
        setCursor(0);
      } catch (_) { /* silent */ }
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Global keyboard shortcut: `/` focuses the search box (unless typing
  // in another field).
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const inField = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (e.key === "/" && !inField) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function goto(r) {
    if (!r) return;
    setOpen(false);
    setQ("");
    navigate(r.href);
  }

  function onKeyDown(e) {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); goto(results[cursor]); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  return (
    <div ref={wrapRef} className="relative w-full max-w-md" data-testid={testid}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search customers, invoices, partners… (/)"
        data-testid={`${testid}-input`}
        className="w-full rounded-full border border-slate-800 bg-slate-900 pl-9 pr-14 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 placeholder:text-slate-500"
      />
      {!open && (
        <kbd className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 px-1.5 py-0.5 rounded border border-slate-700 bg-slate-950 text-[10px] text-slate-500 font-mono pointer-events-none">
          /
        </kbd>
      )}

      {open && q.trim().length >= 2 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute top-full mt-2 left-0 right-0 z-50 rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-slate-950/50 overflow-hidden"
        >
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid={`${testid}-no-results`}>
              No matches for “{q}”.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.id}-${i}`}>
                  <button
                    type="button"
                    onClick={() => goto(r)}
                    onMouseEnter={() => setCursor(i)}
                    data-testid={`${testid}-result-${r.kind}-${r.id}`}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                      cursor === i ? "bg-cyan-500/10" : "hover:bg-slate-950/60"
                    }`}
                  >
                    <ResultIcon kind={r.kind} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-50 font-medium truncate">{r.title}</div>
                      {r.subtitle && <div className="text-xs text-slate-500 truncate">{r.subtitle}</div>}
                    </div>
                    <div className="hidden sm:block text-[10px] text-slate-500 uppercase tracking-widest font-semibold text-right shrink-0">
                      {r.kind}
                      {r.meta && <div className="text-slate-400 mt-0.5 normal-case tracking-normal font-mono">{r.meta}</div>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-slate-800 px-3 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            <span>↑ ↓ to nav</span>
            <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> open</span>
            <span>esc to close</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultIcon({ kind }) {
  const cls = "w-4 h-4 shrink-0";
  if (kind === "customer") return <User2 className={`${cls} text-cyan-400`} />;
  if (kind === "invoice") return <Receipt className={`${cls} text-emerald-400`} />;
  if (kind === "partner") return <Users2 className={`${cls} text-amber-400`} />;
  return <Search className={cls} />;
}
